import React, { FC } from 'react';

import pluralize from 'pluralize';

import { EvFlexColumn, EvFlexRow, EvHeadingLarge, EvTitle } from '@evinced-private/ui-common';

import {
	WFA_DASHBOARD_CONTAINER_DAID,
	WFA_DASHBOARD_LAST_UPDATED_DAID,
	WFA_DASHBOARD_TITLE_DAID
} from 'data-automation-ids/WfaDashboardAutomationIds';
import { AccessibilitySummaryContainer } from 'src/apps/dashboard/components/accessibility-summary/AccessibilitySummaryContainer';
import { BusinessUnitsTable } from 'src/apps/dashboard/components/business-units-table';
import { useBusinessUnitLabelDisplayName } from 'src/apps/dashboard/helpers/BusinessUnitLabelHelper';
import { useEffectiveFilters } from 'src/apps/dashboard/helpers/FilterHelpers';
import { useBusinessUnitsTableData } from 'src/apps/dashboard/hooks/useBusinessUnitsTableData';
import { usePlatformConfigInitializer } from 'src/common/hooks/usePlatformConfigInitializer';
import { DASHBOARD_LABELS } from 'src/common/labels/dashboard.labels';
import { useDashboardFiltersStore } from 'src/common/stores/dashboardFiltersStore';
import { usePlatformConfigStore } from 'src/common/stores/platformConfigStore';

import { AccessibilityCriticalIssuesChartContainer } from '../../components/accessibility-critical-issues-chart/AccessibilityCriticalIssuesChartContainer';
import { AccessibilityTrendsChartContainer } from '../../components/accessibility-trends-chart/AccessibilityTrendsChartContainer';
import { DashboardChartWrapper } from '../../components/dashboard-chart-wrapper';
import { DashboardFilters } from '../../components/dashboard-filters/DashboardFilters';
import { LastUpdated } from '../../components/last-updated';
import { useConfigurationFilters } from '../../hooks/useConfigurationFilters';
import { useDashboardDataQuery } from '../../hooks/useDashboardDataQuery';
import { useScoreDataQuery } from '../../hooks/useScoreDataQuery';

export const AccessibilityDashboard: FC = () => {
	usePlatformConfigInitializer();
	const { filters, setFilters } = useDashboardFiltersStore();
	const { isUniquenessServiceEnabled } = usePlatformConfigStore();

	const { filterDefinitions, filterOptions, labelsExcludedFromDashboard } =
		useConfigurationFilters();
	const effectiveFilters = useEffectiveFilters(filters, filterDefinitions);

	const {
		data: dashboardData,
		isLoading: isDashboardDataLoading,
		error: dashboardDataError
	} = useDashboardDataQuery(filters, labelsExcludedFromDashboard, filterDefinitions);

	const {
		data: scoreData,
		isLoading: isScoreDataLoading,
		error: scoreDataError
	} = useScoreDataQuery(filters, labelsExcludedFromDashboard, filterDefinitions);

	const scoreDataList = scoreData?.scores ?? [];

	const [businessUnitsQuery] = useBusinessUnitsTableData({
		filters,
		filterDefinitions
	});

	const businessUnitsData = businessUnitsQuery.data;
	const isBusinessUnitsLoading =
		businessUnitsQuery.isLoading ||
		businessUnitsQuery.isPreviousData ||
		(businessUnitsQuery.isStale && businessUnitsQuery.isRefetching);
	const businessUnitsError = businessUnitsQuery.error;

	const businessUnitDisplayName = useBusinessUnitLabelDisplayName();

	return (
		<EvFlexColumn gap="XLARGE" dataAutomationId={WFA_DASHBOARD_CONTAINER_DAID}>
			<EvFlexColumn gap="XLARGE">
				<EvFlexColumn gap="MEDIUM">
					<EvHeadingLarge as="h1" dataAutomationId={WFA_DASHBOARD_TITLE_DAID}>
						{DASHBOARD_LABELS.WEB_DASHBOARD_TITLE}
					</EvHeadingLarge>
					<LastUpdated
						firstUpdatedAt={dashboardData?.lastUpdatedAt ?? 0}
						secondUpdatedAt={scoreData?.lastUpdatedAt ?? 0}
						dataAutomationId={WFA_DASHBOARD_LAST_UPDATED_DAID}
					/>
				</EvFlexColumn>
				<DashboardFilters
					filters={filters}
					onFilterChange={setFilters}
					filterDefinitions={filterDefinitions}
					filterOptions={filterOptions}
				/>
			</EvFlexColumn>

			<EvFlexColumn gap="XLARGE">
				<EvFlexColumn role="group" ariaLabelledby="accessibility-summary-heading" as="section">
					<AccessibilitySummaryContainer
						scoreData={scoreDataList}
						uniqueIssuesData={dashboardData?.uniqueIssues}
						totalIssuesData={dashboardData?.totalIssues}
						filters={effectiveFilters}
						isLoading={isDashboardDataLoading || isScoreDataLoading}
						error={dashboardDataError || scoreDataError}
						isUniquenessServiceEnabled={isUniquenessServiceEnabled}
					/>
				</EvFlexColumn>

				<EvFlexColumn gap="LARGE" as="section">
					<EvTitle
						id="trends-heading"
						titleText={DASHBOARD_LABELS.ACCESSIBILITY_PERFORMANCE_TRENDS}
					/>

					<EvFlexRow gap="XLARGE">
						<DashboardChartWrapper>
							<AccessibilityTrendsChartContainer
								scoreData={scoreDataList}
								isLoading={isScoreDataLoading}
								error={scoreDataError}
							/>
						</DashboardChartWrapper>
						<DashboardChartWrapper>
							<AccessibilityCriticalIssuesChartContainer
								data={dashboardData?.dailyAllIssues}
								uniqueIssuesData={dashboardData?.dailyUniqueIssues}
								isLoading={isDashboardDataLoading}
								error={dashboardDataError}
							/>
						</DashboardChartWrapper>
					</EvFlexRow>
				</EvFlexColumn>

				<section className="business-units-section">
					<EvFlexColumn gap="XLARGE">
						<EvTitle
							id="business-units-heading"
							titleText={
								businessUnitDisplayName ? pluralize(businessUnitDisplayName) : 'Business Units'
							}
						/>

						<BusinessUnitsTable
							data={businessUnitsData?.rows ?? []}
							isLoading={isBusinessUnitsLoading}
							error={businessUnitsError}
							total={businessUnitsData?.total ?? 0}
							unfilteredTotal={businessUnitsData?.unfilteredTotal ?? 0}
							isUniquenessServiceEnabled={isUniquenessServiceEnabled}
						/>
					</EvFlexColumn>
				</section>
			</EvFlexColumn>
		</EvFlexColumn>
	);
};
